import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState, } from 'react';
import { usePreferences } from './PreferencesContext';
function getPrimaryKey(invert) {
    if (invert) {
        return 'shift[false]_ctrl[false]_alt[false]';
    }
    return 'shift[true]_ctrl[false]_alt[false]';
}
const defaultKeyModifiersState = {
    ctrlKey: false,
    shiftKey: false,
    altKey: false,
    modifiersKey: null,
};
export const KeyModifierContext = createContext(defaultKeyModifiersState);
export function useKeyModifiers() {
    const context = useContext(KeyModifierContext);
    if (!context) {
        throw new Error('key modifiers context was not found');
    }
    return context;
}
const isMac = navigator !== undefined && navigator.userAgent.toLowerCase().includes('mac');
export function getModifiers(event) {
    const { shiftKey, altKey, metaKey } = event;
    const ctrlKey = isMac ? metaKey : event.ctrlKey;
    return { ctrlKey, shiftKey, altKey };
}
export function toModifiersKey(keyModifiers) {
    const { shiftKey, altKey, ctrlKey } = keyModifiers;
    return `shift[${shiftKey ? 'true' : 'false'}]_ctrl[${ctrlKey ? 'true' : 'false'}]_alt[${altKey ? 'true' : 'false'}]`;
}
function getModifiersKey(event) {
    const keyModifiers = getModifiers(event);
    return toModifiersKey(keyModifiers);
}
export function useMapKeyModifiers() {
    const { current: { general: { invert }, }, } = usePreferences();
    return useMemo(() => {
        const primaryKeyIdentifier = getPrimaryKey(invert);
        return {
            primaryKeyIdentifier,
            getModifiersKey,
        };
    }, [invert]);
}
export function KeyModifiersProvider({ children }) {
    const [modifiers, setModifiers] = useState(defaultKeyModifiersState);
    const { current: { general: { invert }, }, } = usePreferences();
    useEffect(() => {
        function handleKeyDown(event) {
            const keyModifiers = getModifiers(event);
            const modifiersKey = toModifiersKey(keyModifiers);
            setModifiers({ ...keyModifiers, modifiersKey });
        }
        function handleKeyUp(event) {
            const keyModifiers = getModifiers(event);
            const modifiersKey = toModifiersKey(keyModifiers);
            setModifiers({ ...keyModifiers, modifiersKey });
        }
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [invert]);
    return (_jsx(KeyModifierContext.Provider, { value: modifiers, children: children }));
}
